import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import ContentPage from "@/components/contentPage";
import DataSetDetailDataAssetList from "@/components/dataAsset/dataSetDetailDataAssetList";
import { DataSetOverviewCard } from "@/components";
import { dataAssetSearchSchema } from "@/schemas/dataAssetSearchSchema";

const dataSetDetailQueryOptions = (api: any, publicId: string) =>
  queryOptions({
    queryKey: ["dataSetDetail", publicId],
    queryFn: () => api.dataAccess.dataSetDetail({ publicId }),
    enabled: !!publicId,
  });

export const Route = createFileRoute("/data/sets/$publicId/")({
  component: DataSetDetail,
  validateSearch: dataAssetSearchSchema,
  loader: ({ context: { queryClient, api }, params: { publicId } }) =>
    queryClient.prefetchQuery(dataSetDetailQueryOptions(api, publicId)),
});

function DataSetDetail() {
  const { api } = Route.useRouteContext();
  const { publicId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();

  // Force dataset filter to match the current route's publicId
  const currentFilter = search.dataSetPublicIds;
  const needsUpdate = !currentFilter || currentFilter.length !== 1 || currentFilter[0] !== publicId;
  useEffect(() => {
    if (publicId && needsUpdate) {
      navigate({
        search: ((prev: Record<string, unknown>) => ({
          ...prev,
          dataSetPublicIds: [publicId],
        })) as any,
        replace: true,
      });
    }
  }, [publicId, navigate, needsUpdate]);

  const queryClient = useQueryClient();
  const opts = dataSetDetailQueryOptions(api, publicId);
  const { isPending: dataSetDetailIsPending, data: dataSetData } = useQuery(opts);

  const refreshQuery = () => queryClient.invalidateQueries({ queryKey: opts.queryKey });

  if (!dataSetData || dataSetDetailIsPending) return "Loading...";
  return (
    <ContentPage title={`Data Set`}>
      <div className="flex justify-center">
        <div className="w-full 2xl:w-9/12 3xl:w-full">
          <DataSetOverviewCard
            dataSet={dataSetData}
            showDownloadLinks
            showDetailLink={false}
            refreshQuery={refreshQuery}
          />
        </div>
      </div>
      <div className="mt-6 py-4">
        <DataSetDetailDataAssetList
          title="Data Assets"
          shouldGrabFocus={false}
        />
      </div>
    </ContentPage>
  );
}
